import { useContext, useEffect } from 'react';

import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'hooks';

import ValidationMessage from 'components/validation/ValidationMessage';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import { addFile, removeFile } from 'reducers/autoPersonalFormReducer';

import { FILE_TYPE } from 'variables/constants';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import { getHTMLId } from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation153bResolverProps extends ValidationResolverProps {
  failedObjectId: number;
  vehicleLabel: string;
}

const VALIDATION_ID = '153b';
const OSSC_VALIDATION_ID = '153b-ossc';
const FORM_STATE_KEY = 'autoPersonalForm';

const Validation153bResolver = ({
  section,
  itemKey: propItemKey,
  failedObjectId,
  vehicleLabel,
  compact = false,
}: Validation153bResolverProps) => {
  const { uuid } = useParams();
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };
  const dispatch = useAppDispatch();
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';
  const osscItemKey = `vehicle-153b-ossc-${failedObjectId}`;

  const mandatoryFileValidations = getMandatoryFileValidations(FORM_STATE_KEY);

  const validationObject = validations.find(
    (v) => v.validation_id === VALIDATION_ID,
  );

  const alertMessage = getValidationMessage(
    validations,
    VALIDATION_ID,
    failedObjectId,
  );
  const show = !!alertMessage;

  const photosMessage = `(${VALIDATION_ID}) ${validationObject?.validation_name} : ${validationObject?.validation_outcome_data?.type_messages?.vehicle_photos}`;

  const osscMessage = `(${VALIDATION_ID}) ${validationObject?.validation_name} : ${validationObject?.validation_outcome_data?.type_messages?.ossc}`;

  const photosFile = useAppSelector((state) =>
    state.autoPersonalForm.files.find(
      (file) =>
        file.validation_key === VALIDATION_ID && file.item_key === itemKey,
    ),
  );

  const osscFile = useAppSelector((state) =>
    state.autoPersonalForm.files.find(
      (file) =>
        file.validation_key === OSSC_VALIDATION_ID &&
        file.item_key === osscItemKey,
    ),
  );

  useEffect(() => {
    if (show) {
      dispatch(
        addFile({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: FILE_TYPE.TYPE_153b.type,
          files: [],
          text: FILE_TYPE.TYPE_153b.text,
          mandatory: mandatoryFileValidations.includes(VALIDATION_ID),
        }),
      );
      dispatch(
        addFile({
          validation_key: OSSC_VALIDATION_ID,
          item_key: osscItemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type: FILE_TYPE.TYPE_153b_OSSC.type,
          files: [],
          text: FILE_TYPE.TYPE_153b_OSSC.text,
          mandatory: mandatoryFileValidations.includes(OSSC_VALIDATION_ID),
        }),
      );
    } else {
      dispatch(
        removeFile({ validation_key: VALIDATION_ID, item_key: itemKey }),
      );
      dispatch(
        removeFile({
          validation_key: OSSC_VALIDATION_ID,
          item_key: osscItemKey,
        }),
      );
    }
  }, [show, itemKey, osscItemKey, section, mandatoryFileValidations, dispatch]);

  const getCompactAlertStatus = () =>
    [photosFile, osscFile].every(
      (file) => file?.status === 'approved' || file?.is_deferred,
    )
      ? 'success'
      : 'warning';

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  if (!show) return null;

  const body = (
    <>
      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={photosMessage} />
      </Alert>
      {photosFile && (
        <ValidateFile
          uuid={uuid}
          file={photosFile}
          validationId={VALIDATION_ID}
          itemKey={itemKey}
          formStateKey={FORM_STATE_KEY}
          context={photosMessage}
        />
      )}
      {osscFile && (
        <ValidateFile
          uuid={uuid}
          file={osscFile}
          validationId={OSSC_VALIDATION_ID}
          itemKey={osscItemKey}
          formStateKey={FORM_STATE_KEY}
          context={osscMessage}
        />
      )}
      <Box fontSize="sm" color="gray.500" mt="10px">
        Confirm this is for: {vehicleLabel}
      </Box>
    </>
  );

  if (compact) {
    return (
      <>
        <div id={htmlId}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Upload file
            </Button>
          </Alert>
        </Tooltip>
        <Modal isOpen={isCompactOpen} onClose={onCompactClose} size="4xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Vehicle Age (15+ years)</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">{body}</Box>
              <Button
                variant="brand"
                onClick={onCompactClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      <div id={htmlId}></div>
      {body}
    </Box>
  );
};

export default Validation153bResolver;
